/**
 * Client-side rate limiting utility
 * Prevents excessive API calls and provides better UX
 */

interface RateLimitEntry {
  count: number;
  resetTime: number;
}

class ClientRateLimiter {
  private limits: Map<string, RateLimitEntry> = new Map();
  private readonly maxAttempts: number;
  private readonly windowMs: number;

  constructor(maxAttempts: number = 10, windowMs: number = 60000) {
    this.maxAttempts = maxAttempts;
    this.windowMs = windowMs;
    
    // Clean up old entries every minute
    setInterval(() => this.cleanup(), 60000);
  }

  /**
   * Check if an action is rate limited
   * @param key - Unique identifier for the action (e.g., 'share-token-validation')
   * @returns true if action should be allowed, false if rate limited
   */
  checkLimit(key: string): boolean {
    const now = Date.now();
    const entry = this.limits.get(key);

    // No previous attempts or window expired
    if (!entry || now > entry.resetTime) {
      this.limits.set(key, {
        count: 1,
        resetTime: now + this.windowMs,
      });
      return true;
    }

    // Within window, check count
    if (entry.count >= this.maxAttempts) {
      return false;
    }

    // Increment count
    entry.count++;
    return true;
  }

  /**
   * Get time remaining until rate limit resets
   * @param key - Unique identifier for the action
   * @returns milliseconds until reset, or 0 if not rate limited
   */
  getResetTime(key: string): number {
    const entry = this.limits.get(key);
    if (!entry) return 0;
    
    const remaining = entry.resetTime - Date.now();
    return remaining > 0 ? remaining : 0;
  }

  /**
   * Manually reset rate limit for a key
   * @param key - Unique identifier for the action
   */
  reset(key: string): void {
    this.limits.delete(key);
  }

  /**
   * Clean up expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.limits.entries()) {
      if (now > entry.resetTime) {
        this.limits.delete(key);
      }
    }
  }
}

// Export singleton instance for share token validation
// 10 attempts per minute for share token validation
export const shareTokenLimiter = new ClientRateLimiter(10, 60000);

// Export class for custom instances
export default ClientRateLimiter;
